import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import moment from 'moment';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import DateSelectCard, {
  HiddenImageAction,
  RemoveFromCartAction,
} from 'app/components/DateSelectCard';
import FeatureMiniMap from 'app/components/FeatureMiniMap/FeatureMiniMap';
import {isSceneInTab} from 'app/components/OrderImagerySidebar/utils';
import {SceneStatusBadge, SceneStatusOverlay, sceneStatus} from 'app/components/SceneStatus';
import {CartItemV1, isCartEnabled, useCart} from 'app/modules/Cart';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {OrderableScenesLoader} from 'app/providers/OrderableScenesProvider';
import * as featureUtils from 'app/utils/featureUtils';
import {useContinuity, useStateWithDeps} from 'app/utils/hookUtils';
import {getLayerKeyForScene, getThumbnailOverrideScene} from 'app/utils/imageryUtils';

import cs from './ImageryPurchaseDrawer.module.scss';

type TabId = 'imagery' | 'data';

const ImageryPurchaseDrawer: React.FunctionComponent<
  React.PropsWithChildren<{
    year: number;
    features: I.ImmutableOf<ApiFeature[]>;
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;

    orderableScenesLoader: OrderableScenesLoader;
    makeSceneUrl: (featureLensId: string, sourceId: string, date: string) => string;
    blockedSources?: string[];

    onPreviewOrderableScene: (
      feature: I.ImmutableOf<ApiFeature>,
      orderableScene: I.ImmutableOf<ApiOrderableScene>
    ) => void;
    onClose: () => void;
    /** Pass null if the user is not allowed to change visibility */
    setIsImageHidden:
      | null
      | ((
          f: I.ImmutableOf<ApiFeature>,
          orderableScene: I.ImmutableOf<ApiOrderableScene>,
          isHidden: boolean
        ) => void);

    activeFeatureId?: number;
  }>
> = ({
  features,
  organization,
  profile,
  year,
  orderableScenesLoader,
  makeSceneUrl,
  blockedSources,
  onPreviewOrderableScene,
  onClose,
  setIsImageHidden,
  activeFeatureId,
}) => {
  const navigate = useNavigate();
  const {findBySceneKey} = useCart();
  const cartEnabled = isCartEnabled(organization, profile);
  const cartItemFor = (
    feature: I.ImmutableOf<ApiFeature>,
    scene: I.ImmutableOf<ApiOrderableScene>
  ): CartItemV1 | undefined =>
    cartEnabled
      ? findBySceneKey({
          featureId: feature.get('id'),
          sourceId: scene.get('sourceId'),
          sensingTime: scene.get('sensingTime'),
        })
      : undefined;

  // The same badge and hover overlay the order pane uses, so a scene reads the same on both
  // surfaces. The "Ordered" header above these rows says it too, but repeating it costs nothing
  // and saves the drawer a rule of its own.
  const statusProps = (
    scene: I.ImmutableOf<ApiOrderableScene>,
    cartItem: CartItemV1 | undefined
  ) => {
    const isOrdered = scene.get('isOrdered');
    const status = sceneStatus({
      isOrdered,
      isProcessed: scene.get('orderProcessed'),
      isPartial: isOrdered && !scene.get('isFullyOrdered'),
      cartItem,
    });

    return status
      ? {
          thumbnailBadge: <SceneStatusBadge status={status} />,
          thumbnailOverlay: <SceneStatusOverlay status={status} />,
        }
      : {};
  };

  const featureIds = useContinuity(
    features.map((f) => f!.get('id')),
    I.is
  );

  // Resets to the first feature ID if the list of feature IDs changes.
  const [currentFeatureId, setCurrentFeatureId] = useStateWithDeps<number | undefined>(
    activeFeatureId ?? featureIds.first(),
    [featureIds]
  );

  const currentFeature =
    currentFeatureId === undefined
      ? undefined
      : features.find((f) => f!.get('id') === currentFeatureId);

  const currentFeatureLensId = currentFeature?.getIn(['properties', 'lensId']);

  const orderableScenesStatusMaybe =
    currentFeatureId === undefined ? undefined : orderableScenesLoader(currentFeatureId);

  const allOrderableScenes: I.ImmutableOf<ApiOrderableScene[]> =
    orderableScenesStatusMaybe && orderableScenesStatusMaybe.value
      ? orderableScenesStatusMaybe.value
      : I.List();
  const orderableScenes = allOrderableScenes.filter(
    (d) => moment(d!.get('sensingTime')).year() === year
  );

  const [selectedTab, setSelectedTab] = React.useState<TabId>('imagery');
  const [hoveredFeatureId, setHoveredFeatureId] = React.useState<number | null>(null);
  const [showHiddenImagery, setShowHiddenImagery] = React.useState(false);

  // Filter scenes by selected tab
  const tabFilteredScenes = React.useMemo(
    () => orderableScenes.filter((scene) => isSceneInTab(scene, selectedTab)),
    [orderableScenes, selectedTab]
  );
  const tabCounts = React.useMemo(() => {
    const imageryScenesCount = orderableScenes.filter((scene) =>
      isSceneInTab(scene, 'imagery')
    ).size;

    const dataScenesCount = orderableScenes.filter((scene) => isSceneInTab(scene, 'data')).size;

    return {imagery: imageryScenesCount, data: dataScenesCount};
  }, [orderableScenes]);

  const purchasedScenes = React.useMemo(
    () => currentFeature && tabFilteredScenes.filter((d) => d!.get('isOrdered')),
    [tabFilteredScenes, currentFeature]
  );

  const availableScenes = React.useMemo(
    () =>
      currentFeature &&
      tabFilteredScenes.filter(
        (d) => !d?.get('isOrdered') && !featureUtils.isOrderableSceneHidden(currentFeature, d!)
      ),
    [tabFilteredScenes, currentFeature]
  );

  const hiddenScenes = React.useMemo(
    () =>
      currentFeature &&
      tabFilteredScenes?.filter(
        (d) => !d?.get('isOrdered') && featureUtils.isOrderableSceneHidden(currentFeature, d!)
      ),
    [tabFilteredScenes, currentFeature]
  );

  // utility function just for this file's orderableScenes since we call it a bunch below
  // (memoize orderableScenes to avoid recreating on every call)
  const orderableScenesList = React.useMemo(() => orderableScenes.toList(), [orderableScenes]);
  const _getThumbnailOverrideUrlForDrawer = React.useCallback(
    (scene: I.ImmutableOf<ApiOrderableScene>) => {
      const thumbnailOverrideScene = getThumbnailOverrideScene(scene, orderableScenesList);
      const {url: thumbnailOverrideUrl} = thumbnailOverrideScene
        ? featureUtils.imageAndSourceDetailsFromScene(thumbnailOverrideScene)
        : {url: undefined};
      return thumbnailOverrideUrl;
    },
    [orderableScenesList]
  );

  return (
    <B.Drawer isOpen={!!currentFeature} size={460} className={cs.drawer} onClose={() => onClose()}>
      {currentFeature && (
        <>
          <div className={cs.drawerHeader}>
            <B.Button minimal icon="small-cross" onClick={() => onClose()} />
            <h3>{currentFeature.getIn(['properties', 'name'])}</h3>
          </div>

          <div className={cs.tabs}>
            <B.Tabs
              id="imagery-purchase-drawer-tabs"
              selectedTabId={selectedTab}
              onChange={(newTabId: TabId) => setSelectedTab(newTabId)}
            >
              <B.Tab
                id="imagery"
                title={`Imagery (${tabCounts.imagery > 999 ? '999+' : tabCounts.imagery})`}
              />
              <B.Tab id="data" title={`Data (${tabCounts.data > 999 ? '999+' : tabCounts.data})`} />
            </B.Tabs>
          </div>

          <div className={cs.drawerContentWrapper}>
            {currentFeatureId !== undefined && (
              <div className={cs.miniMap}>
                <FeatureMiniMap
                  features={features}
                  selectedFeatureId={currentFeatureId}
                  setSelectedFeatureId={setCurrentFeatureId}
                  hoveredFeatureId={hoveredFeatureId}
                  hoverFeatureById={setHoveredFeatureId}
                  width={420}
                  height={200}
                />

                {features.size > 1 && (
                  <div className={cs.miniMapNote}>{features.size} locations</div>
                )}
              </div>
            )}

            <div className={cs.drawerContent}>
              {(orderableScenesStatusMaybe?.status === 'unknown' &&
                !orderableScenesStatusMaybe?.value) ||
              !availableScenes ||
              !purchasedScenes ||
              !hiddenScenes ? (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                  }}
                >
                  <B.Spinner />
                </div>
              ) : (
                <>
                  {orderableScenesStatusMaybe?.status === 'unknown' && (
                    <div className={cs.loadingScenesSpinner}>
                      <B.Spinner size={B.SpinnerSize.SMALL} />
                      <span>Loading more scenes…</span>
                    </div>
                  )}
                  {tabFilteredScenes.isEmpty() && (
                    <B.NonIdealState className={cs.message} iconMuted={false}>
                      This location does not have any{' '}
                      {selectedTab === 'data' ? 'data' : 'high-res imagery'} for the selected year.
                    </B.NonIdealState>
                  )}

                  {!purchasedScenes.isEmpty() && (
                    <>
                      <h4 className={cs.imageryHeader}>Ordered</h4>

                      {purchasedScenes.toArray().map((scene) => {
                        const layerKey = getLayerKeyForScene(scene!);
                        const thumbnailOverrideUrl = _getThumbnailOverrideUrlForDrawer(scene!);
                        // A partly delivered scene can have its remainder in the cart, so these
                        // rows carry cart state too.
                        const cartItem = cartItemFor(currentFeature, scene!);
                        const sceneUrl = makeSceneUrl(
                          currentFeatureLensId!,
                          scene!.get('sourceId'),
                          scene!.get('sensingTime')
                        );
                        return (
                          <DateSelectCard
                            key={scene!.get('sensingTime') + scene!.get('sourceId')}
                            layerKey={layerKey}
                            orderableScene={scene!}
                            organization={organization}
                            isOrdered
                            blockedSources={blockedSources}
                            renderRightElement={() => (
                              <div className={cs.rowActions}>
                                {cartItem && <RemoveFromCartAction item={cartItem} />}
                                {scene!.get('orderProcessed') && !!layerKey && (
                                  <B.AnchorButton
                                    small
                                    outlined
                                    intent={B.Intent.PRIMARY}
                                    text="View"
                                    // A real href, so cmd- and middle-click open a tab the way
                                    // they do on any link. Plain clicks stay in the app.
                                    href={sceneUrl}
                                    onClick={(ev: React.MouseEvent) => {
                                      if (ev.shiftKey || ev.metaKey || ev.ctrlKey) {
                                        return;
                                      }
                                      ev.preventDefault();
                                      // hack to remove the domain
                                      navigate(sceneUrl.replace(/^https?:\/\/[^/]+/, ''));
                                    }}
                                  />
                                )}
                              </div>
                            )}
                            thumbnailOverrideUrl={thumbnailOverrideUrl}
                            {...statusProps(scene!, cartItem)}
                          />
                        );
                      })}
                    </>
                  )}

                  {!availableScenes.isEmpty() && (
                    <>
                      <h4 className={cs.imageryHeader}>Available to Order</h4>

                      {availableScenes.toArray().map((scene) => {
                        const thumbnailOverrideUrl = _getThumbnailOverrideUrlForDrawer(scene!);
                        const cartItem = cartItemFor(currentFeature, scene!);
                        return (
                          <DateSelectCard
                            key={scene!.get('sensingTime') + scene!.get('sourceId')}
                            layerKey={getLayerKeyForScene(scene!)}
                            orderableScene={scene!}
                            organization={organization}
                            isOrdered={false}
                            blockedSources={blockedSources}
                            renderRightElement={() =>
                              makeDateSelectButton(
                                currentFeature,
                                scene!,
                                false,
                                (orderableScene: I.ImmutableOf<ApiOrderableScene>) =>
                                  onPreviewOrderableScene(currentFeature, orderableScene),
                                setIsImageHidden,
                                cartItem
                              )
                            }
                            thumbnailOverrideUrl={thumbnailOverrideUrl}
                            {...statusProps(scene!, cartItem)}
                          />
                        );
                      })}
                    </>
                  )}

                  {!hiddenScenes.isEmpty() && (
                    <>
                      <div className={cs.imageryHeaderRow}>
                        <h4 className={cs.imageryHeader}>Hidden Imagery</h4>
                        <a
                          onClick={(ev) => {
                            ev.preventDefault();
                            setShowHiddenImagery(!showHiddenImagery);
                          }}
                        >
                          {showHiddenImagery ? 'Hide' : `Show ${hiddenScenes.size}`}
                        </a>
                      </div>

                      {showHiddenImagery &&
                        hiddenScenes.toArray().map((scene) => {
                          const thumbnailOverrideUrl = _getThumbnailOverrideUrlForDrawer(scene!);
                          const cartItem = cartItemFor(currentFeature, scene!);
                          return (
                            <DateSelectCard
                              key={scene!.get('sensingTime') + scene!.get('sourceId')}
                              layerKey={getLayerKeyForScene(scene!)}
                              orderableScene={scene!}
                              organization={organization}
                              isOrdered={false}
                              blockedSources={blockedSources}
                              renderRightElement={() =>
                                makeDateSelectButton(
                                  currentFeature,
                                  scene!,
                                  true,
                                  (orderableScene: I.ImmutableOf<ApiOrderableScene>) =>
                                    onPreviewOrderableScene(currentFeature, orderableScene),
                                  setIsImageHidden,
                                  cartItem
                                )
                              }
                              thumbnailOverrideUrl={thumbnailOverrideUrl}
                              {...statusProps(scene!, cartItem)}
                            />
                          );
                        })}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </B.Drawer>
  );
};

/** Only ever called for rows in the available and hidden sections, which filter out ordered scenes. */
function makeDateSelectButton(
  feature: I.ImmutableOf<ApiFeature>,
  orderableScene: I.ImmutableOf<ApiOrderableScene>,
  isHidden: boolean,
  onPreviewOrderableScene: (orderableScene: I.ImmutableOf<ApiOrderableScene>) => void,
  setIsImageHidden:
    | null
    | ((
        f: I.ImmutableOf<ApiFeature>,
        orderableScene: I.ImmutableOf<ApiOrderableScene>,
        isHidden: boolean
      ) => void),
  cartItem: CartItemV1 | undefined
) {
  return (
    <div className={cs.rowActions}>
      {/* Remove takes the hide slot rather than a second one: visibleOnHover only hides a button,
          it still claims its width. */}
      {cartItem ? (
        <RemoveFromCartAction item={cartItem} />
      ) : (
        setIsImageHidden && (
          <HiddenImageAction
            feature={feature}
            orderableScene={orderableScene}
            isHidden={isHidden}
            setIsImageHidden={setIsImageHidden}
          />
        )
      )}

      <B.Button
        small
        intent={B.Intent.PRIMARY}
        text="Preview"
        onClick={() => onPreviewOrderableScene(orderableScene)}
      />
    </div>
  );
}

export default ImageryPurchaseDrawer;
